import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    catchError,
    distinctUntilChanged,
    EMPTY,
    Subscription,
    switchMap,
    tap,
} from 'rxjs';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import {
    OrganizationBudgetDisplayInformation,
    OrganizationBudgetDto,
    OrganizationCreditContributorDisplay,
    OrganizationCreditContributorDto,
} from '../../model/organization-budget';
import { OrganizationService } from '../../services/organization.service';

@Component({
    selector: 'app-organization-credits-page',
    templateUrl: './organization-credits-page.component.html',
    styleUrl: './organization-credits-page.component.scss',
    standalone: false,
})
export class OrganizationCreditsPageComponent implements OnInit, OnDestroy {
    budget: OrganizationBudgetDisplayInformation | undefined;
    isLoading: boolean = true;
    hasError: boolean = false;
    private userAuthSubscription?: Subscription;

    constructor(
        private organizationService: OrganizationService,
        private translateService: TranslateService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.userAuthSubscription = this.authenticationService
            .observableUserAuth()
            .pipe(
                distinctUntilChanged(
                    (previous, current) =>
                        previous.activeOrganization ===
                        current.activeOrganization
                ),
                tap(() => {
                    this.isLoading = true;
                    this.hasError = false;
                }),
                switchMap(() =>
                    this.organizationService.getOrganizationBudget().pipe(
                        catchError(() => {
                            this.hasError = true;
                            this.isLoading = false;
                            return EMPTY;
                        })
                    )
                )
            )
            .subscribe((budget) => {
                this.budget = this.mapToDisplayInformation(budget);
                this.isLoading = false;
            });
    }

    private mapToDisplayInformation(
        budget: OrganizationBudgetDto
    ): OrganizationBudgetDisplayInformation {
        const total = Math.round(budget.budget.inclusive + budget.budget.extra);
        const used = Math.round(budget.budget.used + budget.budget.blocked);
        const available = total - used;

        const usedPercentage =
            total > 0
                ? Math.round(
                      ((budget.budget.used + budget.budget.blocked) /
                          (budget.budget.inclusive + budget.budget.extra)) *
                          100
                  )
                : 0;
        return {
            periodStartDate: budget.budget.periodStartDate,
            periodEndDate: budget.budget.periodEndDate,
            isUnlimited: budget.budget.isUnlimited,
            total,
            used,
            available,
            usedPercentage:
                budget.budget.isUnlimited || total === 0
                    ? null
                    : usedPercentage,
            groups: this.mapContributors(budget.groups),
            people: this.mapContributors(budget.people),
        };
    }

    private mapContributors(
        contributors: OrganizationCreditContributorDto[] | null | undefined
    ): OrganizationCreditContributorDisplay[] {
        return (contributors ?? []).map((contributor, index) => ({
            rank: index + 1,
            name: contributor.name ?? null,
            creditsUsed: Math.round(contributor.creditsUsed),
        }));
    }

    ngOnDestroy(): void {
        this.userAuthSubscription?.unsubscribe();
    }

    get currentLanguage(): string {
        return this.translateService.getCurrentLang() || 'en';
    }

    get usedWidth(): number {
        if (!this.budget) {
            return 0;
        }

        if (this.budget.isUnlimited || this.budget.total === 0) {
            return 100;
        }

        return Math.min(Math.max(this.budget.usedPercentage ?? 0, 0), 100);
    }

    get availableWidth(): number {
        if (
            !this.budget ||
            this.budget.isUnlimited ||
            this.budget.total === 0 ||
            (this.budget.available ?? 0) <= 0
        ) {
            return 0;
        }

        return 100 - this.usedWidth;
    }

    get showAvailableSegment(): boolean {
        return this.availableWidth > 0;
    }

    get showPercentage(): boolean {
        const usedPercentage = this.budget?.usedPercentage;
        return usedPercentage !== null && usedPercentage !== undefined;
    }
}
